import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronRightLine, CloudDownloadLine} from '@dropbox/dig-icons/assets';
import {aiSearchLoadingGlyphVariantAtom} from 'atoms/aiSearch';
import {CompanyOSGlyphLoading} from 'components/DSYS/CompanyOSLogo';
import {usePendingAction} from 'hooks/usePendingAction';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {ReactNode, useState} from 'react';

import styles from './ChatToolLines.module.css';

/** One thing the agent did, shown as a faint line in the conversation. */
export type ChatToolStep = {
  label: string;
  /** Tool name for analytics (e.g. find_workstreams). */
  toolName?: string;
  /** What the step is doing, e.g. Dash's searches. Shown live while it works, then behind ›. */
  detail?: ReactNode;
  /** What it was asked to do, e.g. its query. Behind ›, even while it works. */
  request?: ReactNode;
  /** Where it looks (CompanyOS, Dash, Knowledge, Slack…), in the line's leading slot. */
  icon?: ReactNode;
  /** The rows it returned, behind ›. */
  response?: ReactNode;
  /** Short text after the label, e.g. "12s". */
  result?: string;
  /** No result yet: still running while the turn streams. */
  isPending?: boolean;
  /** The agent's own bookkeeping (combining, aggregating), grouped into one line. */
  isBookkeeping?: boolean;
  /**
   * Download this step's rows (e.g. as CSV), offered at the end of the line.
   * A returned promise disables the button until it settles; handle its errors.
   */
  onDownload?: () => void | Promise<void>;
  /** Fired when the details are opened or closed. */
  onResponseToggle?: (expanded: boolean) => void;
};

/** Ask's working glyph, sized to the line's leading slot. */
const WorkingGlyph = () => {
  const variant = useAtomValue(aiSearchLoadingGlyphVariantAtom);
  return (
    <span className={styles.glyph}>
      <CompanyOSGlyphLoading variant={variant} />
    </span>
  );
};

/**
 * The leading slot every line shares, so labels line up: Ask's glyph, the
 * step's icon, a chevron, or blank.
 */
export const ChatToolLineSlot = ({
  kind,
  open,
  icon,
}: {
  kind: 'working' | 'icon' | 'chevron' | 'blank';
  open?: boolean;
  icon?: ReactNode;
}) => (
  <span
    className={`${styles.slot} ${open && kind === 'chevron' ? styles.slotOpen : ''}`}
    aria-hidden
  >
    {kind === 'working' ? <WorkingGlyph /> : null}
    {kind === 'icon' ? icon : null}
    {kind === 'chevron' ? <UIIcon src={ChevronRightLine} size="small" /> : null}
  </span>
);

/**
 * A step as one faint line, led by its icon: its label shimmers while it works,
 * then it shows its result. A click opens what it was asked at any time, and
 * once settled what it found, whose rows can be downloaded from the end of the
 * line. Without an icon, a working line shows Ask's glyph and an openable one ›.
 */
export const ChatToolLine = ({
  step,
  working,
  glyph = true,
  shimmer = working,
}: {
  step: ChatToolStep;
  working: boolean;
  /** Ask's glyph while it works, when the step has no icon; off to only shimmer. */
  glyph?: boolean;
  /** Whether its label shimmers while it works; on unless the caller holds it back. */
  shimmer?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const download = usePendingAction(step.onDownload);
  const openable = Boolean(step.request) || (!working && Boolean(step.detail || step.response));

  const toggle = () => {
    setOpen((current) => {
      step.onResponseToggle?.(!current);
      return !current;
    });
  };

  const content = (
    <>
      <ChatToolLineSlot
        kind={step.icon ? 'icon' : working && glyph ? 'working' : openable ? 'chevron' : 'blank'}
        open={open}
        icon={step.icon}
      />
      {/* The result flows after the label's last word, so a long label wraps as one text. */}
      <Text size="small" color="subtle">
        <span className={`${styles.label} ${working && shimmer ? styles.shimmer : ''}`}>
          {step.label}
        </span>
        {step.result ? (
          <>
            {' '}
            <Text size="small" color="faint" className={styles.result}>
              · {step.result}
            </Text>
          </>
        ) : null}
      </Text>
    </>
  );

  return (
    <Box style={{minWidth: 0}}>
      <div className={`${styles.line} ${open ? styles.lineOpen : ''}`}>
        {openable ? (
          <button type="button" className={styles.row} onClick={toggle} aria-expanded={open}>
            {content}
          </button>
        ) : (
          <div className={styles.row}>{content}</div>
        )}
        {!working && step.onDownload ? (
          <Tooltip title={t('chat_tool_line_download')} placement="bottom" openDelay={300}>
            <IconButton
              variant="borderless"
              size="small"
              className={styles.download}
              aria-label={t('chat_tool_line_download')}
              disabled={download.isPending}
              onClick={() => void download.run()}
            >
              <UIIcon
                src={CloudDownloadLine}
                size="small"
                className={atoms({color: 'Text Subtle'})}
              />
            </IconButton>
          </Tooltip>
        ) : null}
      </div>
      {openable && open ? (
        <Stack gap="8" className={styles.body}>
          {step.request}
          {working ? null : step.detail}
          {step.response}
        </Stack>
      ) : null}
      {working && step.detail ? <Box className={styles.body}>{step.detail}</Box> : null}
    </Box>
  );
};
